/**
 * Cached Performance tab (CR-4751).
 *
 * Thin orchestrator. All rendering lives in sub-components under ./cached/.
 *
 * Mirrors DefaultTab's visual layout but fed by nightly-captured KPI snapshots
 * instead of live aggregations. Point-in-time values that do not shift as
 * underlying data changes.
 *
 * Reads scope from ViewContext (site/group) so the tab honours the same
 * top-bar context switcher the live dashboard uses.
 */
import { useMemo, useState } from 'react'
import { Loader2, AlertCircle, Info } from 'lucide-react'

import { Card, CardContent } from '../ui/card'
import { Button } from '../ui/button'

import { usePerformanceHistory, type PerformanceHistoryOptions } from '../../hooks/usePerformanceHistory'
import type { MetricType, PeriodType } from '../../lib/api/dashboard-snapshots'
import type { DeliveryMetricMode } from '../../lib/api'
import { useView } from '../../contexts/ViewContext'
import { PreferencesCurrencyOverride } from '../../contexts/PreferencesContext'
import type { QualityGradeBands } from './SiteKPIComponents'
import { CachedHeader } from './cached/CachedHeader'
import { CachedKPISection } from './cached/CachedKPISection'
import { CachedTrendCharts } from './cached/CachedTrendCharts'
import { CachedArrearsWaterfall } from './cached/CachedArrearsWaterfall'
import { CachedComparisonTable } from './cached/CachedComparisonTable'
import {
  METRIC_LABELS, PERIOD_LOOKBACK_MONTHS, buildLatestMap, monthsAgoISO, notApplicableMetrics,
} from './cached/helpers'

interface CachedPerformanceTabProps {
  /**
   * CR-8697: the customer's Delivery Metric Mode, from the dashboard payload
   * the page already loads — the snapshot API does not carry it. Absent shows both.
   */
  deliveryMetricMode?: DeliveryMetricMode
  /**
   * CR-4751: the currency the live tabs show money in (the dashboard payload's
   * `currency`). Snapshot money comes back converted into it; a EUR site used
   * to read as pounds, unconverted.
   */
  displayCurrency?: string
  /** CR-4751: the customer's PPM colour bands, from the same payload, for the PPM badge. */
  ppmGradeBands?: QualityGradeBands
}

export function CachedPerformanceTab({
  deliveryMetricMode, displayCurrency, ppmGradeBands,
}: CachedPerformanceTabProps = {}) {
  const [period, setPeriod] = useState<PeriodType>('monthly')
  const {
    viewLevel, selectedCompanyName, selectedSiteName, selectedGroupName, getViewQueryParams, viewKey,
  } = useView()

  // CR-4751: the switcher's scope goes with every call and refetches on
  // change — the tab used to keep the previous scope's figures under the new
  // heading until a reload.
  const options: PerformanceHistoryOptions = useMemo(() => {
    const months = PERIOD_LOOKBACK_MONTHS[period] ?? 12
    return {
      periodType: period,
      dateFrom: monthsAgoISO(months),
      scope: getViewQueryParams(),
      currency: displayCurrency,
    }
  }, [period, viewKey, displayCurrency]) // eslint-disable-line react-hooks/exhaustive-deps -- viewKey changes exactly when getViewQueryParams' output does

  const { data, loading, error, trendsError, refetch } = usePerformanceHistory(options)

  // CR-4751: period metrics with no row in the latest capture are N/A —
  // nothing to measure — not the previous period's value.
  const naMetrics = useMemo(
    () => notApplicableMetrics(data?.latest, Object.keys(METRIC_LABELS) as MetricType[], period),
    [data, period],
  )

  // Build the KPI-card map. State metrics (open orders, arrears, etc.) are
  // backfill-skipped by design, so a Weekly/Quarterly view wouldn't surface
  // them at all. Fall back to the daily latest for a missing STATE metric so
  // the card shows "building history" only when there's truly no capture yet.
  // CR-4751: only state rows (no period of their own) — a daily PPM or OTD is
  // not this week's, and used to stand in for an N/A one.
  const latestByMetric = useMemo(() => {
    const map = buildLatestMap(data?.latest)
    naMetrics.forEach((m) => map.delete(m))
    data?.stateLatest?.forEach((s) => {
      if (!map.has(s.metric_type) && !s.period_start) map.set(s.metric_type, s)
    })
    return map
  }, [data, naMetrics])

  // Honest "as of" label: prefer the most recent period_end across the
  // returned snapshots (what users expect — the period the figures cover),
  // and fall back to snapshot_date if no period-based metric is present.
  const latestPeriodEnd = useMemo(() => {
    if (!data?.latest?.length) return null
    const periodEnds = data.latest
      .map((s) => s.period_end)
      .filter((d): d is string => !!d)
      .sort()
    return periodEnds.length ? periodEnds[periodEnds.length - 1] : null
  }, [data])

  // CR-4751: the currency the money came back in — the one asked for, unless
  // there was no rate into it. The charts below format in this one.
  const valuesCurrency = useMemo(
    () => [...(data?.latest ?? []), ...(data?.stateLatest ?? [])]
      .find((s) => s.metric_unit === 'currency' && s.currency)?.currency ?? displayCurrency,
    [data, displayCurrency],
  )

  const scopeLabel = useMemo(() => {
    if (viewLevel === 'GROUP' && selectedGroupName) return selectedGroupName
    if (viewLevel === 'SITE' && selectedSiteName) return selectedSiteName
    return selectedCompanyName || null
  }, [viewLevel, selectedCompanyName, selectedSiteName, selectedGroupName])

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[300px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-sm text-muted-foreground">Loading cached performance data...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
        <div>
          <p className="text-red-800 font-medium">Failed to load cached performance data</p>
          <p className="text-red-700 text-sm mt-1">{error}</p>
          <Button onClick={refetch} size="sm" variant="outline" className="mt-3">Retry</Button>
        </div>
      </div>
    )
  }

  const hasAnySnapshots = (data?.latest.length ?? 0) > 0 || (data?.stateLatest?.length ?? 0) > 0
  const latestDate = data?.latest[0]?.snapshot_date ?? null

  return (
    <div className="space-y-4">
      <CachedHeader
        period={period}
        onPeriodChange={setPeriod}
        latestSnapshotDate={latestDate}
        latestPeriodEnd={latestPeriodEnd}
        scopeLabel={scopeLabel}
      />

      {!hasAnySnapshots && (
        <Card>
          <CardContent className="p-8 text-center">
            <Info className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <p className="font-medium">No snapshots captured yet</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              The nightly capture task will begin populating this tab from its next run.
              Trends and period-over-period deltas appear once at least two snapshots exist.
            </p>
          </CardContent>
        </Card>
      )}

      {hasAnySnapshots && data && (
        <PreferencesCurrencyOverride currency={valuesCurrency}>
          <CachedKPISection
            latestByMetric={latestByMetric}
            trends={data.trends}
            period={period}
            naMetrics={naMetrics}
            deliveryMetricMode={deliveryMetricMode}
            ppmGradeBands={ppmGradeBands}
          />
          {trendsError && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between gap-3">
              <p className="text-sm text-amber-800 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                The trend charts could not be loaded, so they are empty.
              </p>
              <Button onClick={refetch} size="sm" variant="outline">Retry</Button>
            </div>
          )}
          <CachedTrendCharts
            trends={data.trends}
            latestByMetric={latestByMetric}
            targets={data.targets}
            period={period}
            naMetrics={naMetrics}
            deliveryMetricMode={deliveryMetricMode}
          />
          <CachedArrearsWaterfall trends={data.trends} period={period} />
          <CachedComparisonTable
            latest={data.latest} trends={data.trends} period={period} naMetrics={naMetrics}
            deliveryMetricMode={deliveryMetricMode}
          />
        </PreferencesCurrencyOverride>
      )}
    </div>
  )
}

export default CachedPerformanceTab
